import api from "@/api/baseApi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileInput } from "@/components/ui/FileInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toWords } from "@/utils/TowordsConfig";
import { useState } from "react";
import toast from "react-hot-toast";

type Props = {
  collection: CollectionOrder | undefined;
  onCollect: () => void;
};

const Collect: React.FC<Props> = ({ collection, onCollect }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(0);
  const [amountLetter, setAmountLetter] = useState("");
  const [reciept, setReciept] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amountValue = Number(e.target.value);
    setAmount(amountValue);

    // Convert amount to French words
    const amountInWords = toWords.convert(amountValue, { currency: true });
    setAmountLetter(amountInWords);
  };

  const handleCollect = async () => {
    setSubmitting(true);
    let formData = new FormData();
    formData.append("image", reciept as File);
    formData.append("path", "collect-reciepts");
    formData.append(
      "description",
      "collected by collector " + collection?.CreatedBy?.fullname
    );

    try {
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const collect = {
        amount,
        amountLetter,
        status: "Payment Collected",
        fileId: res.data.id.toString(),
      };

      await api.patch(`/collector/orders/${collection?.id}/collect`, collect);
      toast.success("Amount collected successfully");
      onCollect();
      setOpen(false);
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setReciept(file);

    if (file && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImagePreview(null);
    }
  };

  const handleRemoveFile = () => {
    setReciept(null);
    setImagePreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        disabled={collection?.status !== "Awaiting Collection"}
        className="text-white text-sm md-text-base px-4 py-1.5 font-semibold bg-primary rounded-full hover:bg-primary/90 cursor-pointer transition ease-in-out duration-200 disabled:bg-primary/50 disabled:transition-none"
      >
        Collect
      </DialogTrigger>
      <DialogContent className="md:w-[24%] w-[90%] p-10 bg-white">
        <DialogHeader>
          <DialogTitle className="text-base font-medium md:text-lg">
            Collect
          </DialogTitle>
          <div className="grid gap-6 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="amount" className="text-left">
                Amount payed
              </Label>
              <Input
                id="amount"
                defaultValue={0}
                type="number"
                className="col-span-3 text-primary"
                onChange={handleAmountChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="amountLetter" className="text-left">
                Collected amount in letter
              </Label>
              <Input
                id="amountLetter"
                value={amountLetter}
                className="col-span-3 text-primary"
                readOnly
              />
            </div>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="receipt"
                className="text-left text-sm font-normal text-[#717171]"
              >
                Attach a receipt
              </Label>
              <FileInput id="reciept" onChange={handleFileChange} />
            </div>
            {imagePreview && (
              <div className="flex flex-col items-center mt-2 space-y-2">
                <img
                  src={imagePreview}
                  alt="File preview"
                  className="object-cover w-32 h-32 rounded"
                />
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-sm text-red-500 underline hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-row justify-center w-full space-x-4">
            <Button
              className="rounded-[5px] py-4 mt-4 w-full bg-primary text-white hover:bg-primary/90"
              type="button"
              onClick={handleCollect}
              disabled={submitting}
            >
              {submitting ? "Collecting..." : "Collect"}
            </Button>
          </DialogFooter>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export default Collect;
